import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { postCPDataWithoutParams, postCPDataWithParams } from 'src/clinic/services'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { appVersion } from 'src/consumer/services'
import { putDataWithParams } from 'src/consumer/services/featureServices'
import {
  getAccessTokenExpiry,
  getAumhumScreenId,
  getGeoLocation,
  getIpAddressFromLocalStorage,
  getOrCreateAumhumScreenId,
  setAumhumScreenId,
} from 'src/consumer/services/localstorageServices'
import { isCommonResponseSuccessfulV2, sleep } from 'src/mentor/helpers/utilityFunctions'

// Extend dayjs with UTC plugin
dayjs.extend(utc)

export default function useAumhumScreen() {
  const registerScreen = async (flowId, additionalData = {}, forceUpdate = false) => {
    try {
      // Get browser type
      const userAgent = navigator.userAgent
      let appBrowserType = 'unknown'
      if (userAgent.indexOf('Chrome') > -1) {
        appBrowserType = 'chrome'
      } else if (userAgent.indexOf('Safari') > -1) {
        appBrowserType = 'safari'
      } else if (userAgent.indexOf('Firefox') > -1) {
        appBrowserType = 'firefox'
      } else if (userAgent.indexOf('Edge') > -1) {
        appBrowserType = 'edge'
      }

      // Detect if app is PWA or normal browser
      const isPWA =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://')
      const appType = isPWA ? 'pwa' : 'browser'

      // Use a stable, client-generated id (persisted up front) so every registration — including
      // the first and any before a prior response has returned — sends the same screenId and the
      // server upserts instead of inserting a duplicate row.
      const SCREEN_ID = getOrCreateAumhumScreenId()

      // Get location
      const location = getGeoLocation()

      // Get current UTC time in the required format
      const currentTimeUTC = dayjs.utc().format('YYYY-MM-DD HH:mm:ss')

      const accessTokenExpiryInDayJs = !!getAccessTokenExpiry()
        ? dayjs(getAccessTokenExpiry())
        : null

      // Prepare the AumhumScreen object (without screenId as server will generate it)
      const screenData = {
        ...additionalData, // This will include screenDeviceConfigId, chamberRoomId, clinicLocationId, clinicBrandId based on flow
        screenId: SCREEN_ID,
        enabled: true,
        deleted: false,
        appType: appType,
        appBrowserType: appBrowserType,
        flowId: flowId,
        location: location,
        latestAppVersion: appVersion,
        loggedInUserTokenExpires: accessTokenExpiryInDayJs
          ? accessTokenExpiryInDayJs.format(MOMENT_FORMATS.formatThatServerSends)
          : null,
        lastAppVersionUpdateTime: currentTimeUTC,
        lastUserLoggedInTime: currentTimeUTC,
        lastLoggedInUserIpAddress: getIpAddressFromLocalStorage(), // Will be captured server-side
        lastLoggedInUserUserAgent: userAgent,
      }

      console.log('screenData', screenData)
      console.log('additionalData', additionalData)

      // Determine if we're in kiosk portal or clinic assistant portal
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
      const isKioskPortal = currentPath.startsWith('/kiosk')
      
      // Use kiosk-scoped API if in kiosk portal, otherwise use old API
      const apiEndpoint = isKioskPortal
        ? '/api/secure/kiosk/screen/config/register'
        : '/api/secure/screen/config/register'
      
      // Call the register API
      const response = await putDataWithParams(apiEndpoint, screenData, {
        forceUpdateDetails: forceUpdate,
      })

      if (response.status === 200) {
        if (isCommonResponseSuccessfulV2(response)) {
          // Get the screen object from response.data.data.screen
          const screenObject = response.data.data?.screen
          // Keep whatever id the server persisted; fall back to the client id we sent. Guarded so a
          // malformed response never overwrites the stored id with undefined.
          const screenId = screenObject?.screenId || SCREEN_ID

          // Store the screenId in localStorage for later unregistration
          setAumhumScreenId(screenId)

          // Dispatch custom event to notify that screen ID is now available
          window.dispatchEvent(
            new CustomEvent('refreshAppUpdateInterval', {
              detail: { screenId: screenId },
            }),
          )

          return {
            success: true,
            screenId: screenId,
            screenObject: screenObject,
            data: response.data,
          }
        } else {
          // Response was not successful - extract error message
          const errorMessage =
            response.data?.message ||
            response.data?.error ||
            response.data?.data?.message ||
            'Failed to register screen: Server returned unsuccessful response'
          console.error('Screen registration failed:', response.data)
          throw new Error(errorMessage)
        }
      } else {
        console.error('Failed to register screen:', response)
        return { success: false, error: response.data }
      }
    } catch (error) {
      console.error('Error registering screen:', error)
      return { success: false, error: error.message }
    }
  }

  const unregisterScreen = async () => {
    try {
      await unregisterScreenFunction()
    } catch (error) {
      console.error('Error unregistering screen:', error)
      return { success: false, error: error.message }
    }
  }

  return { registerScreen, unregisterScreen }
}

export async function unregisterScreenFunction(keepScreenId = false) {
  const screenId = getAumhumScreenId()
  if (!screenId) {
    console.warn('No screen ID found to unregister')
    return { success: false, error: 'No screen ID found' }
  }

  const response = await postCPDataWithParams('/api/secure/screen/config/unregister', screenId)

  if (response.status === 200) {
    // On logout (keepScreenId), preserve AUMHUM_SCREEN_ID: it's this device's stable screen
    // identity, so a relogin reuses the same registration and room claim instead of registering as
    // a brand-new screen — which would churn records and, with the takeover guard, briefly flag the
    // device as "taken over" by its own prior session whose room claim was still fresh. Explicit
    // device resets/unpair still clear it (default).
    if (!keepScreenId) {
      localStorage.removeItem('AUMHUM_SCREEN_ID')
    }
    await sleep(500)
    return { success: true, data: response.data }
  } else {
    console.error('Failed to unregister screen:', response)
    return { success: false, error: response.data }
  }
}

// public class AumhumScreen {
//     @Id
//     private String screenId; //to be generated by client randomly
//     private boolean enabled = true;
//     private boolean deleted = false;

//     private String appType; // "pwa", "browser"
//     private String appBrowserType; //"chrome", "safari", "firefox" etc
//     private String screenDeviceConfigId;
//     private String flowId; //"remote_doctor",
//     private Location location;
//     private String chamberRoomId;
//     private String clinicLocationId;
//     private String clinicBrandId;
//     private String latestAppVersion;
//     @JsonFormat(shape = JsonFormat.Shape.STRING, pattern = "yyyy-MM-dd HH:mm:ss", timezone = "UTC")
//     @DateTimeFormat(iso = DateTimeFormat.ISO.DATE_TIME)
//     private String lastAppVersionUpdateTime;
//     private String lastLoggedInUserId;
//     @JsonFormat(shape = JsonFormat.Shape.STRING, pattern = "yyyy-MM-dd HH:mm:ss", timezone = "UTC")
//     @DateTimeFormat(iso = DateTimeFormat.ISO.DATE_TIME)
//     private String lastUserLoggedInTime;
//     private String lastLoggedInUserIpAddress;
//     private String lastLoggedInUserUserAgent;
//     @JsonFormat(shape = JsonFormat.Shape.STRING, pattern = "yyyy-MM-dd HH:mm:ss", timezone = "UTC")
//     @DateTimeFormat(iso = DateTimeFormat.ISO.DATE_TIME)
//     protected Instant modifiedTime;
// }
